import dynamic from 'next/dynamic';
import localFont from 'next/font/local';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { IntlErrorCode, NextIntlClientProvider } from 'next-intl';
import { ThemeProvider as ColorModeProvider, useTheme as useColorMode } from 'next-themes';
import { useEffect, useMemo, useState } from 'react';
import styled, { createGlobalStyle, css, ThemeProvider } from 'styled-components';
import DefaultHead from '@/components/default-head';
import Footer from '@/components/footer';
import NavBar from '@/components/navbar';

import 'normalize.css/normalize.css';
// Side effect: isolate MobX before stores are constructed. See configure-mobx.js.
import '@/stores/configure-mobx';

import enabledCurrencies from '@data/enabled-currencies';
import Bugsnag, { ErrorBoundary } from '@/bugsnag';
import { ChunkLoadRecoveryFallback, ChunkLoadReloadPrompt } from '@/components/chunk-load-recovery';
import Analytics from '@/components/consent/analytics';
import config from '@/config';
import useDappProvider from '@/hooks/use-dapp-provider';
import { StoreProvider } from '@/stores/store-provider';
import theme, { legacyTheme, media, resolveTheme } from '@/themes';
import { baseStyles, tokens } from '@/themes/tokens';
import {
  clearChunkReloadFlag,
  isChunkLoadError,
  recoverFromChunkLoadError,
} from '@/utils/chunk-load-error';
import { installTranslateDomGuard } from '@/utils/translate-dom-guard';

if (typeof window !== 'undefined') {
  installTranslateDomGuard();
}

// Switzer (variable), self-hosted. Exposed to CSS as --font-switzer in GlobalStyle.
const switzer = localFont({
  src: [
    { path: '../fonts/Switzer-Variable.woff2', weight: '100 900', style: 'normal' },
    { path: '../fonts/Switzer-VariableItalic.woff2', weight: '100 900', style: 'italic' },
  ],
  variable: '--font-switzer',
  display: 'swap',
});

const Cart = dynamic(() => import('@/components/cart'), { ssr: false });
const HelpScoutChat = dynamic(() => import('@/components/help-scout-chat'), {
  ssr: false,
});
const Toaster = dynamic(() => import('react-hot-toast').then((mod) => mod.Toaster), {
  ssr: false,
});
const countries = require('@data/countries.json');
const categories = require('@data/cats.json');

export const GlobalStyle = createGlobalStyle`
  ${tokens}

  :root {
    /* On <html>, not a wrapper, so portals (cart, toasts, menus) get the font too. */
    --font-switzer: ${switzer.style.fontFamily};
    font-size: min(max(16px, 2vw), 18px);
    line-height: 1.25;
  }

  /* A dapp provider's brand colour replaces the primary token as well as theme.colors.primary. */
  ${({ $brandColor }) =>
    $brandColor &&
    css`
      :root:root {
        --primary: ${$brandColor};
        --ring: ${$brandColor};
      }
    `}

  *, *::after, *::before {
    box-sizing: border-box;
  }

  body {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  ${baseStyles}

  /*
   * Through the theme rather than the tokens directly: storefront pages resolve
   * to var(--background) / var(--foreground), forced-light and self-themed pages
   * (unsupported-platform, dapp) keep their original hex page colours.
   */
  body {
    background: ${(props) => props.theme.colors.background.primary};
    color: ${(props) => props.theme.colors.typography.primary};
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: ${(props) => props.theme.fonts.title};
    font-weight: 600;
    letter-spacing: -0.02em;
  }

  h1 {
    font-size: 2em;
    margin: .5em 0;

    ${media.sm`
      font-size: 1.75em;
    `};
  }

  h2 {
    font-size: 1.5em;
    margin: .5em 0;

    ${media.sm`
      font-size: 1.125em;
    `};
  }

  a {
    text-decoration: none;
  }

  p {
    line-height: 1.5;
    margin: 1em 0;
  }

  .toast {
    .toast-body {
      font-family: ${({ theme }) => theme.fonts.primary};
      font-weight: 400;
      font-size: 14px;
      color: var(--primary-foreground);
    }

    .primary-background {
      background: ${(props) => props.theme.colors.primary};
    }

    .error-background {
      background: var(--destructive);
    }
  }

  .wallet-adapter-dropdown {
    .wallet-adapter-button {
      line-height: 1;
      border-radius: 9999px;
      background: rgb(34 197 94/1);

      &:hover {
        background: rgb(34 197 94/1)
      }
    }
  }

  /* Keep the Help Scout Beacon (loaded on demand) above cart and page chrome */
  #beacon-container {
    z-index: 2147483000;
  }
`;

const AppContainer = styled.div`
  max-width: 100vw;
  position: relative;
`;

// The navbar is sticky (in flow), so the content needs no top offset.
const MainContent = styled.div`
  min-height: 100vh;
`;

const MaintenanceBanner = styled.div`
  background: var(--brand-yellow);
  color: var(--ink);
  padding: 1.15em 1.25em;
  font-size: 1.05em;
  font-weight: 700;
  line-height: 1.35;
  letter-spacing: -0.01em;
  text-align: center;

  span {
    display: block;
  }

  span + span {
    margin-top: 0.55em;
    font-weight: 600;
  }
`;

const THEME_COLORS = { light: '#ffffff', dark: '#121212' };

/** Browser chrome colour: follows the OS until the active theme is known, then the theme. */
const ThemeColorMeta = () => {
  const { forcedTheme, resolvedTheme } = useColorMode();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const active = forcedTheme || (mounted ? resolvedTheme : null);
  const color = THEME_COLORS[active];

  return (
    <Head>
      <meta
        content={color || THEME_COLORS.light}
        key="theme-color-light"
        media={color ? undefined : '(prefers-color-scheme: light)'}
        name="theme-color"
      />
      <meta
        content={color || THEME_COLORS.dark}
        key="theme-color-dark"
        media={color ? undefined : '(prefers-color-scheme: dark)'}
        name="theme-color"
      />
    </Head>
  );
};

const toastOptions = {
  style: {
    background: 'var(--popover)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-lg)',
    color: 'var(--popover-foreground)',
    fontSize: '14px',
  },
};

const MyApp = ({ Component, pageProps }) => {
  const router = useRouter();
  const [showChunkReloadPrompt, setShowChunkReloadPrompt] = useState(false);
  const dappProvider = useDappProvider();
  const brandColor = dappProvider?.brandColor;
  // Default theme on SSR + first client paint; apply provider accent after mount.
  const [activeBrandColor, setActiveBrandColor] = useState(null);

  useEffect(() => {
    setActiveBrandColor(brandColor || null);
  }, [brandColor]);

  /*
   * Colour mode, per page (static props on the page component):
   *   Page.forcedTheme = 'light'  pins the page to one theme (unsupported-platform).
   *   Page.selfThemed = true      the page sets html.dark itself (the dapp: provider theme,
   *                               xAppStyle, OS). next-themes is pointed at a throwaway
   *                               attribute there so the two never fight over the class.
   * Both kinds keep the legacy hex theme: their components do colour maths on theme values
   * (polished, hex-alpha suffixes, QR codes), which design-token var() strings would break.
   * Checkout follows the storefront theme (tokens) and shares UI with the dapp, which paints
   * checkout surfaces with CSS variables that already switch on html.dark.
   */
  const forcedTheme = Component.forcedTheme;
  const selfThemed = Boolean(Component.selfThemed);
  const baseTheme = forcedTheme || selfThemed ? legacyTheme : theme;
  const appTheme = useMemo(
    () => resolveTheme(activeBrandColor ? { brandColor: activeBrandColor } : null, baseTheme),
    [activeBrandColor, baseTheme]
  );

  const isWindows =
    typeof window !== 'undefined' ? navigator.userAgent.toLowerCase().includes('windows') : false;

  const handleIntlError = (error) => {
    if (error.code !== IntlErrorCode.MISSING_MESSAGE) {
      Bugsnag.notify(error);
    }
  };

  const handleMessageFallback = ({ namespace, key, error }) => {
    if (process && process.env.NODE_ENV !== 'production') {
      return `[missing key] ${namespace}.${key}`;
    }

    if (error.code === IntlErrorCode.MISSING_MESSAGE) {
      Bugsnag.notify(
        new Error(`[missing key] ${namespace}.${key} - ${router.asPath} - ${router.pathname}`)
      );
      return key;
    }
  };

  useEffect(() => {
    // Page views are sent by <Analytics />, which waits for cookie consent.
    const handleRouteChange = () => {
      // Successful client navigation means the new build is healthy —
      // allow a future deploy to auto-reload again. Do not clear on mount
      // alone, or a still-missing chunk after reload would loop.
      clearChunkReloadFlag();
    };

    const handleChunkFailure = (error) => {
      if (!isChunkLoadError(error)) return false;
      const result = recoverFromChunkLoadError();
      if (result === 'prompt') {
        setShowChunkReloadPrompt(true);
      }
      return true;
    };

    const handleRouteChangeError = (err) => {
      if (err?.cancelled) return;
      handleChunkFailure(err);
    };

    const handleUnhandledRejection = (event) => {
      if (handleChunkFailure(event.reason)) {
        event.preventDefault();
      }
    };

    router.events.on('routeChangeComplete', handleRouteChange);
    router.events.on('routeChangeError', handleRouteChangeError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
      router.events.off('routeChangeError', handleRouteChangeError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [router.events]);

  // Add/remove body class for Windows custom scrollbars
  useEffect(() => {
    if (typeof document !== 'undefined') {
      if (isWindows) {
        document.body.classList.add('use-custom-scrollbars');
      } else {
        document.body.classList.remove('use-custom-scrollbars');
      }
    }
  }, [isWindows]);

  // The maintenance flag is baked into this build. An open tab never sees the
  // deploy that turns it off, so reload until that deploy replaces this page.
  useEffect(() => {
    if (!config.inMaintenanceMode) return undefined;
    const id = window.setInterval(
      () => {
        window.location.reload();
      },
      3 * 60 * 1000
    );
    return () => window.clearInterval(id);
  }, []);

  return (
    <div>
      <ThemeProvider theme={appTheme}>
        <ErrorBoundary FallbackComponent={ChunkLoadRecoveryFallback}>
          <NextIntlClientProvider
            locale={router.locale}
            // Already filtered and filled in by loadMessages (src/lib/messages.js).
            messages={pageProps.messages}
            onError={handleIntlError}
            getMessageFallback={handleMessageFallback}
            timeZone="America/Edmonton"
          >
            <ColorModeProvider
              attribute={selfThemed ? 'data-storefront-theme' : 'class'}
              defaultTheme="system"
              disableTransitionOnChange
              enableColorScheme={!selfThemed}
              enableSystem
              forcedTheme={forcedTheme}
            >
              <DefaultHead />
              {!selfThemed && <ThemeColorMeta />}

              <GlobalStyle $brandColor={activeBrandColor} />
              <StoreProvider pageProps={pageProps}>
                <AppContainer>
                  {!pageProps.hideHeader && <NavBar pageProps={pageProps} />}
                  <MainContent id="main" role="main">
                    {config.inMaintenanceMode && (
                      <MaintenanceBanner>
                        <span>Ordering is paused while we upgrade our systems.</span>
                        <span>We&apos;ll be back shortly.</span>
                      </MaintenanceBanner>
                    )}
                    <Component
                      {...pageProps}
                      currencies={enabledCurrencies}
                      countries={countries}
                      categories={categories}
                    />
                  </MainContent>
                  {!pageProps.hideFooter && <Footer />}
                  <Cart />
                  <Toaster toastOptions={toastOptions} />
                  {showChunkReloadPrompt ? <ChunkLoadReloadPrompt asOverlay /> : null}
                </AppContainer>
                <HelpScoutChat />
              </StoreProvider>
              {/* Cookie consent UI; loads gtag.js only once analytics is allowed. */}
              <Analytics />
            </ColorModeProvider>
          </NextIntlClientProvider>
        </ErrorBoundary>
      </ThemeProvider>
    </div>
  );
};

export default MyApp;
